import React from "react";
import {
  Stack,
  PrimaryButton,
  Callout,
  DirectionalHint,
  Fabric,
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  IDetailsListProps,
  IDetailsRowStyles,
  DetailsRow,
  IColumn,
} from "office-ui-fabric-react";
import { useParams } from "react-router-dom";
import {
  ISLANotes,
} from "../../../vm";
import { ToastContext } from "../../common/ToastProvider";
import { childrenGap } from "../../common/Constants";
import Icon from "@mdi/react";
import {
  mdiDotsVertical,
} from "@mdi/js";
import { Loading } from "../../../services/Loading";
import NoDataTile from "../../common/NoDataTile";
import {
  parseJwt,
} from "../../../services/UtilService";
import { deleteSLANotes, getSLANotes } from "../../../services/sla-matrix/SlaMatrixService";
import { ConfirmDialogContext } from "../../common/ConfirmDialogProvider";
import ManageSlaNotesDialog from "./ManageSlaNotesDialog";

export interface SlaNotesProps { }

const SlaNotes: React.FC<SlaNotesProps> = () => {
  //custom hooks
  const params: any = useParams();
  const { showToast } = React.useContext(ToastContext);
  const { showConfirmDialog } = React.useContext(ConfirmDialogContext);
  const tokenData = parseJwt();

  //state
  const [state, setState] = React.useState({
    isLoading: false,
    searchObj: {
      customerId: params.customerId,
    },
    slaNotes: [],
    manageSlaDialog: {
      isOpen: false,
      index: undefined,
      data: undefined,
    },
    manageActionsCallout: {
      isOpen: false,
      index: undefined,
      element: undefined,
      data: undefined,
    },
  } as {
    isLoading: boolean;
    searchObj: {
      customerId: string,
    };
    slaNotes: ISLANotes[];
    manageSlaDialog: {
      isOpen: boolean;
      index?: number;
      data?: ISLANotes;
    };
    manageActionsCallout: {
      isOpen: boolean;
      element: any;
      index?: number;
      data?: ISLANotes;
    };
  });

  React.useEffect(() => {
    const asyncFunc = async () => {
      await getSLAMatrixRemarksList();
    };
    asyncFunc();
  }, []);


  const getSLAMatrixRemarksList = async () => {
    let localState = { ...state };

    setState({ ...localState, isLoading: true });
    let result = await getSLANotes(localState.searchObj);
    if (result?.success) {
      setState({ ...localState, slaNotes: result.data, isLoading: false })
    } else {
      setState({ ...state, isLoading: false });
      showToast(
        result.message || "Error while getting sla notes",
        "error"
      );
    }
  };

  const handleDialogOpen = (isOpen: boolean, index?: number) => {
    let slaRemarks = undefined;
    if (index !== undefined) {
      slaRemarks = { ...state.slaNotes[index] };
    }
    setState({
      ...state,
      manageSlaDialog: {
        isOpen: isOpen,
        index: index,
        data: slaRemarks,
      },
    });
  };

  const handleDialogClose = (data?: ISLANotes) => {
    let slaRemarks = [...state.slaNotes];
    if (data) {
      if (state.manageSlaDialog.index !== undefined) {
        slaRemarks[state.manageSlaDialog.index] = data;
        showToast("SLA Notes updated successfully", "success");
      } else {
        slaRemarks.unshift(data);
        showToast("SLA Notes added successfully", "success");
      }
    }
    setState({
      ...state,
      manageSlaDialog: {
        isOpen: false,
        index: undefined,
        data: undefined,
      },
      slaNotes: slaRemarks,
    });
  };

  const deleteSLARemarks = async (item: ISLANotes, index: number) => {
    showConfirmDialog(
      "Are You Sure",
      "Do you want to delete SLA Notes",
      async () => {
        setState({
          ...state,
          isLoading: true,
          manageActionsCallout: {
            isOpen: false,
            index: undefined,
            element: undefined,
            data: undefined,
          },
        });
        let result = await deleteSLANotes(item);
        if (result?.success) {
          showToast("SLA Notes deleted successfully", "success");
          let slaRemarks = [...state.slaNotes];
          slaRemarks.splice(index, 1);
          setState({
            ...state,
            slaNotes: slaRemarks,
            isLoading: false,
            manageActionsCallout: {
              isOpen: false,
              index: undefined,
              element: undefined,
              data: undefined,
            },
          });
        } else {
          setState({
            ...state,
            isLoading: false,
            manageActionsCallout: {
              isOpen: false,
              index: undefined,
              element: undefined,
              data: undefined,
            },
          });
          showToast(
            result.message || "Error while deleting SLA Notes",
            "error"
          );
        }
      }
    );
  };

  const handleCallOutOpenClose = (
    isOpen: boolean,
    ele?: React.MouseEvent<HTMLSpanElement>,
    index?: number,
    data?: ISLANotes
  ) => {
    setState({
      ...state,
      manageActionsCallout: {
        isOpen,
        element: isOpen ? ele?.currentTarget : undefined,
        index,
        data,
      },
    });
  };

  const handleLoading = () => {
    setState({
      ...state,
      isLoading: !state.isLoading,
    });
  };

  const _renderItemColumn = (item: any, index: number, column: IColumn) => {
    switch (column.key) {
      case "actions":
        return (
          <span
            onClick={(ele) => handleCallOutOpenClose(true, ele, index, item)}
          >
            <Icon
              path={mdiDotsVertical}
              size={0.9}
              className="pointer"
              color="#135c8a"
            />
          </span>
        );
      case "imageUrl":
        return (
          <img
            src={item.imageUrl}
            alt={`sla-image`}
            className="sla-table-image"
          />
        );
      default:
        return <span>{column.fieldName ? item[column.fieldName] : ""}</span>;
    }
  };

  const _onRenderRow: IDetailsListProps["onRenderRow"] = (props) => {
    const customStyles: Partial<IDetailsRowStyles> = {};
    if (props) {
      if (props.itemIndex % 2 !== 0) {
        customStyles.root = { backgroundColor: "#F3F3F3" };
      }

      return <DetailsRow {...props} styles={customStyles} />;
    }
    return null;
  };

  return (
    <React.Fragment>
      {state.isLoading && <Loading />}
      <Stack tokens={childrenGap}>
        <Stack.Item>
          <Stack
            horizontal
            horizontalAlign="end"
            wrap
            tokens={childrenGap}
          >
            {tokenData.isAdmin && [0, 2, 5].includes(tokenData.role) && (
              <Stack.Item>
                <PrimaryButton
                  text="Add SLA Notes"
                  onClick={() => handleDialogOpen(true)}
                  className="borderless-btn bg-white"
                />
              </Stack.Item>
            )}
          </Stack>
        </Stack.Item>
        <Stack.Item>
          {state.slaNotes && state.slaNotes.length > 0 ? (
            <Fabric>
              <DetailsList
                items={state.slaNotes}
                columns={
                  tokenData.isAdmin && [0, 2, 5].includes(tokenData.role)
                    ? [
                      {
                        key: "imageUrl",
                        name: "Image",
                        fieldName: "imageUrl",
                        minWidth: 150,
                        maxWidth: 250,
                        isResizable: true,
                      },
                      {
                        key: "remarks",
                        name: "Remarks",
                        fieldName: "remarks",
                        minWidth: 400,
                        maxWidth: 600,
                        isResizable: true,
                      },
                      {
                        key: "actions",
                        name: "Actions",
                        fieldName: "actions",
                        minWidth: 80,
                        maxWidth: 130,
                        isResizable: true,
                      },
                    ]
                    : [
                      {
                        key: "image",
                        name: "Image",
                        fieldName: "imageUrl",
                        minWidth: 150,
                        maxWidth: 250,
                        isResizable: true,
                      },
                      {
                        key: "remarks",
                        name: "Remarks",
                        fieldName: "remarks",
                        minWidth: 400,
                        maxWidth: 600,
                        isResizable: true,
                      },
                    ]
                }
                compact={true}
                setKey="set"
                onRenderItemColumn={_renderItemColumn as any}
                layoutMode={DetailsListLayoutMode.justified}
                selectionMode={SelectionMode.none}
                onRenderRow={_onRenderRow}
              />
            </Fabric>
          ) : (
            <NoDataTile title="No SLA Notes Found for this Customer"></NoDataTile>
          )}
        </Stack.Item>
        {state.manageActionsCallout.isOpen && (
          <Callout
            styles={{ root: { width: 95, padding: 20 } }}
            role="alertdialog"
            ariaLabelledBy={
              state.manageActionsCallout && state.manageActionsCallout.data?._id
            }
            gapSpace={0}
            target={state.manageActionsCallout.element}
            directionalHint={DirectionalHint.leftCenter}
            onDismiss={() => handleCallOutOpenClose(false)}
          // setInitialFocus={true}
          >
            <Stack tokens={childrenGap}>
              <Stack.Item className="pointer">
                <span
                  onClick={() => {
                    handleDialogOpen(true, state.manageActionsCallout.index);
                  }}
                  className="callout-options"
                >
                  Edit
                </span>
              </Stack.Item>
              <Stack.Item className="pointer">
                <span
                  onClick={() => {
                    deleteSLARemarks(
                      state.manageActionsCallout.data as ISLANotes,
                      state.manageActionsCallout.index as number
                    );
                  }}
                  className="callout-options"
                >
                  Delete
                </span>
              </Stack.Item>
            </Stack>
          </Callout>
        )}
      </Stack>
      {state.manageSlaDialog.isOpen && (
        <ManageSlaNotesDialog
          data={state.manageSlaDialog.data}
          handleLoading={handleLoading}
          customerId={params.customerId}
          handleDialogClose={(data?: ISLANotes) => handleDialogClose(data)}
        />
      )}
    </React.Fragment>
  );
};

export default SlaNotes;
